import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppIconComponent } from './app-icon.component';

export type ActionType =
  | 'view'
  | 'edit'
  | 'delete'
  | 'create'
  | 'save'
  | 'cancel';

/**
 * Action Button microcomponent for common CRUD operations
 * Provides pre-configured buttons with consistent styling and icons
 */
@Component({
  selector: 'app-action-button',
  standalone: true,
  imports: [CommonModule, AppIconComponent],
  template: `
    <button
      [type]="buttonType"
      [disabled]="isDisabled || isLoading"
      [class]="getButtonClasses()"
      [attr.aria-label]="ariaLabel || getDefaultAriaLabel()"
      [title]="title || getDefaultTitle()"
      (click)="handleClick($event)"
    >
      @if (isLoading) {
      <div class="inline-flex items-center justify-center w-4 h-4 mr-2">
        <div
          class="animate-spin rounded-full h-3 w-3 border-b-2 border-white"
        ></div>
      </div>
      } @else if (getActionIcon()) {
      <app-icon
        [name]="getActionIcon()"
        [color]="getIconColor()"
        [size]="iconSize"
        class="mr-2"
      >
      </app-icon>
      } @if (showLabel) {
      <span>{{ label || getDefaultLabel() }}</span>
      }
    </button>
  `,
})
export class AppActionButtonComponent {
  /** Action type that determines icon, colors, and default labels */
  @Input({ required: true }) action!: ActionType;

  /** Custom label text */
  @Input() label?: string;

  /** Button size */
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  /** Button variant style */
  @Input() variant: 'solid' | 'outline' | 'ghost' = 'solid';

  /** Whether to show loading state */
  @Input() isLoading: boolean = false;

  /** Whether button is disabled */
  @Input() isDisabled: boolean = false;

  /** Whether to show label text */
  @Input() showLabel: boolean = true;

  /** Icon size */
  @Input() iconSize: 'xs' | 'sm' | 'md' = 'sm';

  /** Custom CSS classes */
  @Input() class: string = '';

  /** Accessibility label */
  @Input() ariaLabel?: string;

  /** Tooltip text */
  @Input() title?: string;

  /** Button type attribute */
  @Input() buttonType: 'button' | 'submit' | 'reset' = 'button';

  /** Click event emitter */
  @Output() clicked = new EventEmitter<MouseEvent>();

  /**
   * Handle button click
   */
  handleClick(event: MouseEvent): void {
    if (!this.isDisabled && !this.isLoading) {
      this.clicked.emit(event);
    }
  }

  /**
   * Get icon name for action type
   */
  getActionIcon(): string {
    const iconMap: Record<ActionType, string> = {
      view: 'visibility',
      edit: 'edit',
      delete: 'delete',
      create: 'add',
      save: 'save',
      cancel: 'close',
    };
    return iconMap[this.action];
  }

  /**
   * Get default label for action type
   */
  getDefaultLabel(): string {
    const labelMap: Record<ActionType, string> = {
      view: 'Visualizar',
      edit: 'Editar',
      delete: 'Excluir',
      create: 'Criar',
      save: 'Salvar',
      cancel: 'Cancelar',
    };
    return labelMap[this.action];
  }

  /**
   * Get default aria label
   */
  getDefaultAriaLabel(): string {
    const ariaMap: Record<ActionType, string> = {
      view: 'Visualizar item',
      edit: 'Editar item',
      delete: 'Excluir item',
      create: 'Criar novo item',
      save: 'Salvar alterações',
      cancel: 'Cancelar operação',
    };
    return ariaMap[this.action];
  }

  /**
   * Get default title
   */
  getDefaultTitle(): string {
    return this.getDefaultAriaLabel();
  }

  /**
   * Get icon color based on action
   */
  getIconColor(): 'primary' | 'error' | 'gray' {
    switch (this.action) {
      case 'delete':
        return 'error';
      case 'view':
        return 'gray';
      default:
        return 'primary';
    }
  }

  /**
   * Get computed CSS classes
   */
  getButtonClasses(): string {
    const baseClasses = [
      'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      this.getSizeClasses(),
      this.getVariantClasses(),
      this.class,
    ];

    return baseClasses.filter(Boolean).join(' ');
  }

  /**
   * Get size-based classes
   */
  private getSizeClasses(): string {
    const sizeMap = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
    };
    return sizeMap[this.size];
  }

  /**
   * Get variant and action-based classes
   */
  private getVariantClasses(): string {
    const actionColorMap = {
      delete: {
        solid: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
        outline:
          'border border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500',
        ghost: 'text-red-600 hover:bg-red-50 focus:ring-red-500',
      },
      view: {
        solid: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
        outline:
          'border border-gray-600 text-gray-600 hover:bg-gray-50 focus:ring-gray-500',
        ghost: 'text-gray-600 hover:bg-gray-50 focus:ring-gray-500',
      },
      default: {
        solid:
          'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
        outline:
          'border border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
        ghost: 'text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
      },
    };

    const colorKey =
      this.action === 'delete'
        ? 'delete'
        : this.action === 'view'
        ? 'view'
        : 'default';

    return actionColorMap[colorKey][this.variant];
  }
}
